<template>
    <main>
        <h3>{{ $t('doorbell.title') }}</h3>
        <p class="sub-title">{{ $t('doorbell.rich_notification_sub_title') }}</p>

        <plan-subscribe-section
            @ready="planSectionReady = true"
            @subscribe="handleCloudPlanSubscribe"
            :productsArgs="{ legacy: '', page: 'subscribe' }"
            :filter-rich-notification="true"
            :filter-by-device-model-id="currentDevice?.model?.id || 0"
            :no-bottom-margin="true"
            :defaultSelectedUids="currentDevice ? [currentDevice.uid] : []"
        />
        <div v-if="richPlanDataList.length > 0" class="rich-notification-section">
            <p class="section-title">{{ $t('doorbell.rich_notification_free_title') }}</p>
            <div
                v-for="data in richPlanDataList"
                :key="data.plan.id"
                class="rich-plan-item"
            >
                <i18n
                    path="doorbell.rich_notification_plan_desc"
                    tag="p"
                    class="rich-plan-desc"
                    :places="getRichPlanDescPlaces(data)"
                >
                    <span
                        place="subscribe"
                        class="subscribe-link"
                        @click="handleRichPlanSubscribe(data.plan)"
                    >
                        {{ $t('doorbell.rich_notification_plan_subscribe') }}
                    </span>
                </i18n>
            </div>
        </div>

        <div v-if="!ready" class="loading-overlay">
            <ReoProcessing />
        </div>
    </main>
</template>

<script lang="ts">
import { getDeviceList } from '@/api/cloud';
import { getCloudProductLists } from '@/api/reolink/cart';
import { EPlanStatus, ESeriesCategory, IGetCloudProductsResponse, IPlan, IPlanSeries, IReolinkDevice } from '@reolink-cloud/interface';
import nProgress from 'nprogress';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import PlanSubscribeSection from '@/components/PlanSubscribeSection/index.vue';
import { ESubscribeMode } from '@/interface';
import { getYmhFromQuery } from '@/common/js/query';
import { requestWithErrorHandler } from '@/common/js/utils';
import { animatedPlanFilter, doorbellProgressStorage } from '@/utils/business';
import ReoProcessing from '@/basic/processing.vue';

export default Vue.extend({
    components: {
        PlanSubscribeSection,
        ReoProcessing,
    },
    data() {

        return {
            planSectionReady: false,
            pageDataLoaded: false,
            uid: getYmhFromQuery( this.$route ),
            productData: {} as { cloudProductLists: IGetCloudProductsResponse; },
            currentDevice: undefined as IReolinkDevice | undefined,
            richPlanDataList: [] as Array<{ plan: IPlan; series: IPlanSeries; }>,
        };

    },
    computed: {
        ...mapGetters<'lang'|'country'>( ['lang', 'country'] ),
        ready(): boolean {

            return this.planSectionReady && this.pageDataLoaded;

        },
    },
    async created() {

        await requestWithErrorHandler( async () => {

            this.productData = await getCloudProductLists( this.lang, this.country, { legacy: '', page: 'subscribe' });
            if ( this.uid ) {

                const deviceList: IReolinkDevice[] = await getDeviceList();
                this.currentDevice = deviceList.find( item => item.uid === this.uid );

            }
            this.buildRichPlanDataList();
            this.pageDataLoaded = true;

        });

    },
    mounted() {

        nProgress.done();

    },
    methods: {
        buildRichPlanDataList() {

            if ( !this.productData?.cloudProductLists ) return;

            const { planSeries, plans, deviceModelGroups, countryGroups } = this.productData.cloudProductLists;

            const richSeriesList = Object.values( planSeries as Record<string, IPlanSeries> )
                .filter( series => series.category === ESeriesCategory.CLOUD_RICH_NOTIFICATION_PLAN );

            if ( richSeriesList.length === 0 ) return;

            const dataList: Array<{ plan: IPlan; series: IPlanSeries; }> = [];

            for ( const series of richSeriesList ) {

                const seriesPlans = Object.values( plans as Record<string, IPlan> )
                    .filter( plan => plan.seriesKey === series.key )
                    .filter( animatedPlanFilter )
                    .filter( plan => plan.status === EPlanStatus.ENABLED );

                for ( const plan of seriesPlans ) {

                    if ( this.currentDevice && series.deviceGroup ) {

                        const deviceGroup = deviceModelGroups?.[series.deviceGroup];
                        if ( !deviceGroup?.modelIds?.includes( this.currentDevice.model.id )) {

                            continue;

                        }

                    }

                    if ( this.country && series.countryGroups?.length ) {

                        const countryMatch = series.countryGroups.some(( cg: string ) =>
                            countryGroups?.[cg]?.countries?.some(( c: { key: string; }) => c.key === this.country )
                        );
                        if ( !countryMatch ) continue;

                    }

                    dataList.push({ plan, series });

                }

            }

            this.richPlanDataList = dataList;

        },
        getRichPlanDescPlaces( data: { plan: IPlan; series: IPlanSeries; }): Record<string, string | number> {

            const { plan, series } = data;
            const dailyLimit = plan.style['rich_notification.daily_limit'];
            const notificationTypeKey = plan.style['cloud_storage.rich_notification'] === 'static_and_dynamic_image'
                ? 'doorbell.rich_notification_type_gif'
                : 'doorbell.rich_notification_type_snapshot';

            return {
                planName: series.i18n.name,
                price: parseFloat( plan.pricing.salePrice ) === 0
                    ? String( this.$t( 'plan.free' ))
                    : `${plan.pricing.currency} ${plan.pricing.salePrice}`,
                dailyLimit: typeof dailyLimit === 'number' && dailyLimit > 0
                    ? dailyLimit
                    : String( this.$t( 'plan.unlimited' )),
                notificationType: String( this.$t( notificationTypeKey )),
                deviceCount: plan.style['cloud_storage.max_device_quantity'] ?? 1,
            };

        },
        async handleCloudPlanSubscribe( args: any ) {

            const { plan, selectedDevicesURL } = args;
            doorbellProgressStorage.setItem( 'doorbell_progress_uid', this.uid );
            await this.$router.push({
                path: '/checkout',
                query: {
                    p_id: plan.id,
                    subNew: '1',
                    mode: ESubscribeMode.CREATE,
                    selected_devices: selectedDevicesURL,
                },
            });

        },
        async handleRichPlanSubscribe( plan: IPlan ) {

            const richSelectedDevicesURL = this.currentDevice
                ? encodeURI( JSON.stringify({
                    uid: this.currentDevice.uid,
                    modelTitle: this.currentDevice.model.title,
                    videoRetentionDays: 0,
                }))
                : '';
            doorbellProgressStorage.setItem( 'doorbell_progress_uid', this.uid );
            await this.$router.push({
                path: '/checkout',
                query: {
                    p_id: plan.id,
                    subNew: '1',
                    mode: ESubscribeMode.CREATE,
                    ...( richSelectedDevicesURL ? { selected_devices: richSelectedDevicesURL } : {}),
                },
            });

        },
    },
});
</script>

<style lang="less" scoped>
main {
    background: @white;
    padding: 48px 20px;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    gap: 32px;
    position: relative;

    > h3 {
        font-size: 24px;
        font-weight: 600;
        line-height: 31.2px;
        color: @font_primary;
        text-align: center;
        margin: 0;
    }
}

.sub-title {
    font-size: 16px;
    font-weight: 600;
    line-height: 22.4px;
    color: @font_primary;
    margin: 0;
}

.rich-notification-section {
    display: flex;
    flex-direction: column;
    gap: 24px;

    .section-title {
        font-size: 16px;
        font-weight: 600;
        line-height: 22.4px;
        color: @font_primary;
        margin: 0;
    }

    .rich-plan-item {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .rich-plan-desc {
        font-size: 14px;
        line-height: 19.6px;
        color: @font_primary;
        margin: 0;
        white-space: pre-line;
    }

    .subscribe-link {
        text-decoration: underline;
        cursor: pointer;
    }
}

@media @pc {
    main {
        max-width: 800px;
        margin: 0 auto;
    }
}

.loading-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: @white;
    z-index: 10;
}
</style>
